import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Box,
    Flex,
    Button,
    Stack,
    Menu,
    MenuItem,
    MenuList,
    MenuButton,
} from "@chakra-ui/react";
import {
    FaEdit,
    FaFileAlt,
    FaTrashAlt,
    FaFileUpload,
    FaFileExcel,
} from "react-icons/fa";
import { AddIcon } from "@chakra-ui/icons";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import ConfirmDialog from "components/Dialog/ConfirmDialog";
import Loading from "components/Loading/Loading";
import SearchTable2 from "components/Tables/SearchTable2";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { getEmptyPageResponse, toTablePageableData } from "utils";
import { parkingCredentialsService } from "services/parkingCredentialsService";
import { ParkingCredentialsFilter } from "./ParkingCredentialsFilter";
import { IoIosArrowDown } from "react-icons/io";
import { ROUTES } from "constants/routes-path";
import { PARKING_CREDENTIALS_LIST_URL_DEFAULTS } from "utils/listUrlSync";
import { useListUrlSync } from "hooks/useListUrlSync";
import { ListFiltersActiveAlert } from "components/ListFiltersActiveAlert/ListFiltersActiveAlert";

function ParkingCredentialsList() {
    const { t } = useTranslation();
    const { searchParams, setSearchParams, hasActiveFilters } = useListUrlSync(PARKING_CREDENTIALS_LIST_URL_DEFAULTS);
    const [response, setResponse] = useState(undefined);
    const showToast = useCustomToast();
    const navigate = useNavigate();
    const dialogRef = useRef(undefined);
    const [selectedParkingCredential, setSelectedParkingCredential] = useState(null);
    const [isLoadingExport, setIsLoadingExport] = useState(false);

    const handleExportData = async () => {
        try {
            setIsLoadingExport(true);
            const { data, headers } = await parkingCredentialsService.export();
            const fileName = headers['content-disposition'].match(/filename="(.+)"/)[1];

            const url = URL.createObjectURL(new Blob([data]));
            Object.assign(document.createElement('a'), {
                href: url,
                download: fileName
            }).click();

            URL.revokeObjectURL(url);

        } catch (error) {
            showToast({ title: t(error), status: "error" });
        }
        finally {
            setIsLoadingExport(false);
        }
    }

    const columns = useMemo(() => [
        {
            Header: t("parking-credentials.list.table.header.cpf"),
            accessor: "cpf",
            id: "cpf_beneficiario"
        },
        {
            Header: t("parking-credentials.list.table.header.plate"),
            accessor: "placa",
        },
        {
            Header: t("parking-credentials.list.table.header.name"),
            accessor: "nome",
            id: "nome_beneficiario"
        },
        {
            Header: t("parking-credentials.list.table.header.photo"),
            accessor: "foto",
            disableSortBy: true,
        },
        {
            Header: t("parking-credentials.list.table.header.date"),
            accessor: "data",
            id: "data_criacao"
        },
        {
            Header: t('parking-credentials.list.table.header.operations'),
            disableSortBy: true,
            actions: [
                {
                    label: t('system.actions.edit'),
                    onClick: (row) => { navigate(ROUTES.parkingCredentialsEditPath.replace(":id", row.original.id)) },
                    icon: <FaEdit />
                },
                {
                    label: t('parking-credentials.list.table.actions.documents'),
                    onClick: (row) =>
                        navigate(`${ROUTES.parkingCredentialsDocumentsPath}/?parent_id=${row.original.id}`),
                    icon: <FaFileAlt />
                },
                {
                    label: t('parking-credentials.list.table.actions.additional-documents'),
                    onClick: (row) =>
                        navigate(`${ROUTES.documentsPath}/?parent_id=${row.original.id}&parent_type=credenciais-estacionamento`),
                    icon: <FaFileUpload />
                },
                {
                    label: t('system.actions.remove'),
                    onClick: (row) => { handleRemove(row.original.id) },
                    icon: <FaTrashAlt color="#E53E3E" />,
                }
            ]
        }
    ], []);

    const fetchData = async (params = {}) => {
        params.orderBy = params.orderBy ?? searchParams.orderBy ?? "data_criacao";
        params.orderDirection = params.orderDirection ?? searchParams.orderDirection ?? "desc";
        if (response) setResponse(undefined);

        try {
            params = { ...searchParams, ...params };
            setSearchParams(params);
            const data = await parkingCredentialsService.list(params);
            setResponse(data);
        } catch (err) {
            setResponse(getEmptyPageResponse(searchParams));
            showToast({ title: t(err), status: "error" });
        }
    };

    const clearParkingCredentialsFilters = () => {
        fetchData({
            page: 1,
            filters: {
                placa: null,
                nome_beneficiario: null,
                cpf_beneficiario: null,
                data_inicial: null,
                data_final: null,
            },
        });
    };

    const handleRemove = (parkingCredentialId) => {
        setSelectedParkingCredential(parkingCredentialId);
        dialogRef.current?.displayDialog({
            title: t("parking-credentials.list.table.actions.remove-parking-crendential"),
            message: t("system.actions.remove-confirm"),
        });
    };

    const handleConfirmRemove = async () => {
        if (selectedParkingCredential) {
            try {
                await parkingCredentialsService.delete(selectedParkingCredential);
                showToast({ title: t('parking-credentials.messages.success.delete'), status: "success" });
                fetchData();
            } catch (err) {
                showToast({ title: t(err), status: "error" });
            }
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const newParkingCredential = () => {
        navigate(ROUTES.parkingCredentialsAddPath);
    }

    return (
        <Flex gap="5" direction="column" pt={{ base: "150px", sm: "125px", lg: "90px" }}>
            <Flex w="100%" px="30px" justify="space-between" align="center" direction={{ sm: "column", md: "row" }}>
                <Button
                    variant="list-primary"
                    leftIcon={<AddIcon fontSize="sm" />}
                    alignSelf={{ sm: "flex-start", lg: undefined }}
                    onClick={newParkingCredential}
                >
                    {t("parking-credentials.actions.new")}
                </Button>
                <Stack
                    direction='row'
                    spacing='10px'
                    pt={{ sm: "20px", md: "0",  lg: "0" }}
                    alignSelf={{ sm: 'flex-start', lg: 'auto' }}
                >
                    <ParkingCredentialsFilter
                        filterSnapshot={searchParams.filters}
                        onFilter={(filters) => fetchData({ filters })}
                    />
                    {/*Extra actions menu*/}
                    <Menu>
                        <MenuButton
                            w="125px"
                            variant="list-primary"
                            rightIcon={<IoIosArrowDown />}
                            as={Button}
                        >
                            {t("system.labels.actions")}
                        </MenuButton>
                        <MenuList variant="light">
                            <MenuItem onClick={handleExportData} icon={<FaFileExcel />}>
                                {t("parking-credentials.actions.export")}
                            </MenuItem>
                        </MenuList>
                    </Menu>
                </Stack>
            </Flex>
            <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                <ListPageCardHeader title={t("parking-credentials.title")} subtitle={t("parking-credentials.description")} />
                <CardBody>
                    <ListFiltersActiveAlert show={hasActiveFilters} onClear={clearParkingCredentialsFilters} />
                    {response ? (
                        <Box position="relative">
                            <SearchTable2
                                columnsData={columns}
                                tableData={response.content}
                                pageableData={toTablePageableData(response, { defaultField: "data_criacao", ...searchParams })}
                                onChange={fetchData}
                                disableSearch={true}
                            />
                            {(isLoadingExport &&
                                <Box
                                    position="fixed"
                                    top="0"
                                    left="0"
                                    right="0"
                                    bottom="0"
                                    bg="rgba(255, 255, 255, 0.6)"
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    zIndex="overlay"
                                >
                                    <Loading maxH={{ sm: "100px", md: "350px" }} />
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Loading maxH={{ sm: "100px", md: "350px" }} />
                    )}
                </CardBody>
            </Card>

            <ConfirmDialog ref={dialogRef} onConfirm={handleConfirmRemove} confirmButtonProps={{ colorScheme: "red" }} />
        </Flex>
    );
}

export default ParkingCredentialsList;